import React, { ReactNode, useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { usePanel } from 'contexts/panelContext';
import { Icon } from 'components/_new/Icon';
import { HorizontalActionList } from 'components/_new/HorizontalActionList/HorizontalActionList';
import rightArrowIcon from 'assets/icons/right-arrow.svg';
import { useAuth } from 'contexts/authContext';
import { slugify } from 'utils/helpers';
import { ContentParserOptions } from 'utils/contentParser';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { FavoriteIcon } from './FavoriteIcon';
import { ReadedIcon } from './ReadedIcon';
import { ToreadIcon } from './ToreadIcon';
import { ArticlePresenterDialog } from './ArticlePresenterDialog';
import { RecommendedArticlesSkeleton } from './skeletons/RecommendedArticlesSkeleton';
import { KnowledgeBaseArticleContentItem, KnowledgeBaseRecommendedArticlesByCategory } from '../../types/KnowledgeBase';

interface RecommendedArticlesProps {
	renderWrapper?: (hasArticles: boolean, children: ReactNode) => ReactNode;
	contentParserOptions?: ContentParserOptions;
}

export const RecommendedArticles: React.FC<RecommendedArticlesProps> = ({ renderWrapper, contentParserOptions }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();
	const { knowledgeBaseLinkPrefix, context, queries } = useKnowledgeBase();

	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
	const [modalArticleId, setModalArticleId] = useState<number | null>(null);

	const { data, isLoading, refetch } = useQuery<KnowledgeBaseRecommendedArticlesByCategory>({
		queryKey: [
			'KNOWLEDGE_BASE_RECOMMENDED',
			{
				applicationId: Number(currentApplicationData?.id),
				language: currentUser?.language,
				context,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		enabled: true,
		queryFn: () => queries.getRecommendedArticles(),
		staleTime: 5 * 60 * 1000,
		cacheTime: 10 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	useEffect(() => {
		if (data?.[0]) {
			setSelectedCategoryId(data[0].categoryId);
		}
	}, [data]);

	const categories = data?.map(({ categoryId, name }) => ({ categoryId, name })) || [];

	const categoryArticles =
		selectedCategoryId && data
			? data.find(({ categoryId }) => categoryId === selectedCategoryId)?.articles || []
			: [];

	const goNextCategory = () => {
		if (!data || data.length === 0) return;

		const currentIdx = data.findIndex(({ categoryId }) => categoryId === selectedCategoryId);
		if (currentIdx >= 0 && Boolean(data?.[currentIdx + 1])) {
			const next = data[currentIdx + 1];
			setSelectedCategoryId(next.categoryId);
		}
	};

	const hasNextCategory = useMemo(() => {
		if (!data || data.length === 0) return false;
		const currentIdx = data.findIndex(({ categoryId }) => categoryId === selectedCategoryId);
		return currentIdx >= 0 && Boolean(data?.[currentIdx + 1]);
	}, [data, selectedCategoryId]);

	const openArticle = (article: KnowledgeBaseArticleContentItem) => {
		const { articleId, slug } = article;
		const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${slug ? `-${slugify(slug)}` : ''}`;
		switch (context) {
			case 'coordinator':
			case 'program-admin':
				navigate({
					pathname,
				});
				break;
			case 'user-panel':
			default:
				setModalArticleId(articleId);
				break;
		}
	};

	if (isLoading) {
		return <RecommendedArticlesSkeleton />;
	}

	const hasArticles = Boolean(categories.length > 0);

	const content = (
		<>
			{context === 'user-panel' && (
				<ArticlePresenterDialog
					articleId={modalArticleId}
					visible={modalArticleId !== null}
					onHide={() => setModalArticleId(null)}
					onFlagChange={() => refetch()}
					knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
					contentParserOptions={contentParserOptions}
				/>
			)}

			<div className="flex flex-column gap-4">
				<div className="flex flex-row gap-2 overflow-x-auto p-1">
					{categories.map(({ categoryId, name }) => {
						const isSelected = selectedCategoryId === categoryId;
						return (
							<Button
								key={categoryId}
								label={name}
								variant={isSelected ? 'new-purple' : 'new-gray'}
								onClick={() => setSelectedCategoryId(categoryId)}
								className="text-nowrap"
								rounded
							/>
						);
					})}
				</div>

				<HorizontalActionList
					itemsClassName="flex flex-row gap-2 justify-content-start align-content-stretch"
					onSwipeAction={goNextCategory}
					swipeDisabled={!hasNextCategory}
					maxSwipeWidth={150}
					renderAction={(swipePerc) => (
						<div className="w-full flex flex-column gap-1 justify-content-center align-items-center overflow-hidden text-purple text-center">
							<img src={rightArrowIcon} className="w-full h-auto" style={{ maxWidth: 80 }} alt="arrow" />
							<span style={{ width: 80, opacity: swipePerc }}>{t('actions.goFurther')}</span>
						</div>
					)}
				>
					{categoryArticles.map((item) => {
						const { id, articleId, title, slug, info } = item;
						const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
							slug ? `-${slugify(slug)}` : ''
						}`;
						return (
							<Link
								to={{ pathname }}
								key={id}
								className="flex article-tile"
								onClick={(event) => {
									event.preventDefault();
									openArticle(item);
								}}
								tabIndex={-1}
							>
								<Box
									variant="white-bordered"
									className="w-full"
									hoverable
									img={info?.imageUrl || 'default'}
								>
									<BoxSection horizontal contentClassName="justify-content-between">
										<div className="inline-flex gap-2 align-items-center">
											{context === 'user-panel' && (
												<>
													<FavoriteIcon checked={info.flag.favorite} />
													<ToreadIcon checked={info.flag.toRead} />
													<ReadedIcon checked={info.flag.readed} />
												</>
											)}
											{['program-admin', 'coordinator'].includes(context) && (
												<>
													{info?.access?.mentor && (
														<span
															title={t('knowledgeBase.programPanel.article.mentorAccess')}
														>
															<Icon name="user-tie" className="text-purple-500 text-lg" />
														</span>
													)}
													{info?.access?.mentee && (
														<span
															title={t('knowledgeBase.programPanel.article.menteeAccess')}
														>
															<Icon
																name="user-graduate"
																className="text-blue-500 text-lg"
															/>
														</span>
													)}
												</>
											)}
										</div>
										<div className="inline-flex gap-1 align-items-center text-muted text-lg text-nowrap">
											<Icon name="clock" iconStyle="regular" />
											{info.timeToRead || 15} m
										</div>
									</BoxSection>

									<BoxSection className="flex-1">
										<span className="strong">{title}</span>
									</BoxSection>

									<BoxSection>
										<Button
											label={t('actions.open')}
											variant="gray-primary"
											block
											onClick={(event) => {
												event.stopPropagation();
												event.preventDefault();
												openArticle(item);
											}}
										/>
									</BoxSection>
								</Box>
							</Link>
						);
					})}
				</HorizontalActionList>
			</div>
		</>
	);

	if (renderWrapper) {
		return <>{renderWrapper(hasArticles, content)}</>;
	}

	if (!hasArticles) {
		return null;
	}

	return content;
};
